import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { DateTypeDJ } from '../../../../@types/Company';
import { setSelectedCompany } from '../../../../store/AuthReducer';
import { useAppDispatch } from '../../../../store/hook';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import { showToast } from '../../../common/Notification';
import type { ImportData } from './ImportResultBody';
import ImportResultBody from './ImportResultBody';

interface Company {
  id: number;
  name: string;
  siren: string;
  /** @deprecated */
  paidAccessUntil: string;
  subscriptions?: {
    communicating?: {
      startDate: DateTypeDJ | null;
      endDate: DateTypeDJ | null;
    };
    prospecting?: {
      startDate: DateTypeDJ | null;
      endDate: DateTypeDJ | null;
    };
    monitoring?: {
      startDate: DateTypeDJ | null;
      endDate: DateTypeDJ | null;
    };
  };
  commission: number;
}

interface CreatedBy {
  firstname: string;
  lastname: string;
}

export interface ImportResult {
  id: number;
  type: number;
  data: ImportData;
  company: Company;
  createdBy: CreatedBy;
  createdAt: string;
}

interface ImportResultDisplayProps {
  importResult: ImportResult;
}

const formatDate = (dateString: string) => new Date(dateString).toLocaleString('fr-FR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const extractDateString = (dateValue: DateTypeDJ | string | null): string | null => {
  if (!dateValue) {
    return null;
  }
  if (typeof dateValue === 'string') {
    return dateValue;
  }
  if (typeof dateValue === 'object' && 'date' in dateValue) {
    return dateValue.date;
  }
  return null;
};

const getSubscriptionsStatus = (subscribedCompany: Company): string => {
  if (!subscribedCompany.subscriptions) {
    return subscribedCompany.paidAccessUntil ? formatDate(subscribedCompany.paidAccessUntil) : 'N/A';
  }

  const modules: string[] = [];

  if (subscribedCompany.subscriptions.communicating?.endDate) {
    const endDateStr = extractDateString(subscribedCompany.subscriptions.communicating.endDate);
    if (endDateStr) {
      modules.push(`Communication : ${formatDate(endDateStr)}`);
    }
  }

  if (subscribedCompany.subscriptions.prospecting?.endDate) {
    const endDateStr = extractDateString(subscribedCompany.subscriptions.prospecting.endDate);
    if (endDateStr) {
      modules.push(`Prospection : ${formatDate(endDateStr)}`);
    }
  }

  if (subscribedCompany.subscriptions.monitoring?.endDate) {
    const endDateStr = extractDateString(subscribedCompany.subscriptions.monitoring.endDate);
    if (endDateStr) {
      modules.push(`Surveillance : ${formatDate(endDateStr)}`);
    }
  }

  return modules.length > 0 ? modules.join(', ') : 'Aucun abonnement';
};

const ImportResultDisplay: React.FC<ImportResultDisplayProps> = ({ importResult }) => {
  const {
    data, company, createdBy, createdAt,
  } = importResult;
  const jwt = useSelector((state: RootState) => state.auth.token);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleVerifyCopros = async(companyId: number) => {
    try {
      const response = await fetch(`/api/companies/${companyId}`, {
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error fetching company data');
      }

      const companyData = await response.json();
      dispatch(setSelectedCompany(companyData));
      navigate(ROUTES.ADMIN_COPRO_VALIDATION);
    } catch (error) {
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      console.error('Error fetching company data :', error);
    }
  };

  const handleNavigateToCompany = (companyId: number) => {
    navigate(ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', companyId.toString()));
  };

  return (
    <div className="mt-12">
      <div className="rounded-lg p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Résultat de l'import</h2>
          <button
            onClick={() => handleVerifyCopros(company.id)}
            className="rounded bg-rgpd-green px-2 py-1 text-base text-white shadow-md transition-colors hover:bg-rgpd-green/80"
          >
            <CheckCircleIcon className="mr-2 mb-1 inline h-4 w-4" />
            <span className="inline">
              Aller vérifier les copropriétés
            </span>
          </button>
        </div>
        <p className="mb-4 text-gray-600">
          Import effectué le
          {' '}
          {formatDate(createdAt)}
          {' '}
          par
          {' '}
          <b>
            {createdBy.firstname}
            {' '}
            {createdBy.lastname}
          </b>
          .
        </p>
        <div className="mt-6 mb-6 flex items-center justify-between">
          <div className="space-y-2">
            <p>
              <strong className="font-medium">Import effectué pour le cabinet :</strong>
              {' '}
              {company.name}
            </p>
            <p>
              <strong className="font-medium">Abonnements :</strong>
              {' '}
              {getSubscriptionsStatus(company)}
            </p>
          </div>
          <button
            onClick={() => handleNavigateToCompany(company.id)}
            className="rounded bg-rgpd-blue-dark px-2 py-1 text-base text-white shadow-md transition-colors hover:bg-rgpd-blue-dark/80"
          >
            Accéder à la fiche entreprise
          </button>
        </div>
        <ImportResultBody data={data} />
      </div>
    </div>
  );
};

export default ImportResultDisplay;
